import Head from 'expo-router/head';
import { useCallback, useState } from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { listSeedScript } from '@/components/detail/list-seed';
import { HubCityChips } from '@/components/riviera/hub-city-chips';
import { HubFilterStatus, HubFilters, tourFilterGroups } from '@/components/riviera/hub-filters';
import {
  Faq,
  MoreLink,
  SectionHeading,
  landingStyles,
} from '@/components/riviera/landing-parts';
import { LoadMoreButton } from '@/components/riviera/load-more-button';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { TourCard } from '@/components/riviera/tour-card';
import { Fonts, MaxContentWidth, Spacing } from '@/constants/theme';
import { useCityListingPages } from '@/hooks/use-city-listing-pages';
import { useFilteredHub } from '@todo/core/hooks/use-filtered-hub';
import { useTheme } from '@/hooks/use-theme';
import { useTours } from '@todo/core/hooks/use-tours';
import { DEFAULT_TOUR_FILTERS, isDefaultFilters, type TourFilters } from '@todo/core/hub-filters';
import { fetchFilteredTours, fetchToursPage } from '@todo/core/hub-pages';
import { cityToursCopy } from '@/lib/i18n/city-listings-content';
import { hubCopy } from '@/lib/i18n/hub-content';
import { localizedTourTitle } from '@todo/core/i18n/tour-content';
import { winePageCopy } from '@/lib/i18n/wine-content';
import { useLocale } from '@todo/core/i18n/use-locale';
import { CITY_LISTING_PAGES_SEED_KEY, TOURS_INDEX_QUERY, TOURS_INDEX_SEED_KEY } from '@todo/core/list-queries';
import {
  absoluteUrl,
  breadcrumbJsonLd,
  faqPageJsonLd,
  hreflangLinks,
  itemListJsonLd,
  richResultImage,
  toursCrumb,
} from '@todo/core/seo';

export default function ToursScreen() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const listingPages = useCityListingPages();
  const { tours, loading, error } = useTours(TOURS_INDEX_QUERY, TOURS_INDEX_SEED_KEY);
  const [filters, setFilters] = useState<TourFilters>(DEFAULT_TOUR_FILTERS);
  const hub = useFilteredHub(
    tours,
    fetchToursPage,
    filters,
    isDefaultFilters(filters, DEFAULT_TOUR_FILTERS),
    fetchFilteredTours,
  );
  const more = hub.more;
  const setFilter = useCallback(
    (id: string, value: string) => setFilters((prev) => ({ ...prev, [id]: value })),
    [],
  );
  const resetFilters = useCallback(() => setFilters(DEFAULT_TOUR_FILTERS), []);

  const canonical = absoluteUrl(locale, 'tours');
  // Represents the hub with its top listing's photo rather than a generic
  // brand image — real, current content beats a static placeholder.
  const ogImage = richResultImage(tours[0]?.image_url);
  // Hand-written, evergreen SEO copy — see hub-content.ts.
  const copy = hubCopy(locale, 'tours');
  // The regional wine hub is always exported (it's in the header nav).
  const wineTitle = winePageCopy(locale)?.title;
  // "Tours & Excursions in Nice"-style anchors to every exported city page —
  // stronger anchor text than the chips' bare city names.
  const cityLinks = listingPages.tours.flatMap((id) => {
    const cityCopy = cityToursCopy(locale, id);
    return cityCopy ? [{ id, label: cityCopy.title }] : [];
  });

  return (
    <View style={[styles.root, { backgroundColor: theme.background }]}>
      {/* key forces a fresh mount on locale change — see the comment in
          app/[lang]/_layout.tsx for why. */}
      <Head key={locale}>
        <title>{t('seo.toursTitle')}</title>
        <meta name="description" content={t('seo.toursDescription')} />
        <link rel="canonical" href={canonical} />
        {hreflangLinks('tours').map((link) => (
          <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
        ))}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t('seo.toursTitle')} />
        <meta property="og:description" content={t('seo.toursDescription')} />
        <meta property="og:url" content={canonical} />
        {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        <meta name="twitter:title" content={t('seo.toursTitle')} />
        <meta name="twitter:description" content={t('seo.toursDescription')} />
        {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd(locale, [{ name: toursCrumb(locale).name }]))}
        </script>
        {tours.length > 0 ? (
          <script type="application/ld+json">
            {JSON.stringify(
              itemListJsonLd(
                tours.map((tour) => ({
                  name: localizedTourTitle(tour, locale),
                  url: absoluteUrl(locale, `tours/${tour.id}`),
                })),
                canonical,
              ),
            )}
          </script>
        ) : null}
        <script type="application/ld+json">{JSON.stringify(faqPageJsonLd(copy.faq))}</script>
        {/* Baked rows so the client's first render matches this static HTML. */}
        {listSeedScript(TOURS_INDEX_SEED_KEY)}
        {listSeedScript(CITY_LISTING_PAGES_SEED_KEY)}
      </Head>
      <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
        <SiteHeader />
      </SafeAreaView>
      <ScrollView
        style={{ flex: 1 }}>
        <View style={styles.centered}>
          <View style={styles.page}>
            {/* role="heading" + aria-level renders a real <h1> on web (via
                react-native-web) while staying valid on native — this file is
                shared by both platforms. The page had no heading element at
                all before. */}
            <Text
              role="heading"
              aria-level={1}
              style={[styles.heading, { color: theme.text, fontFamily: Fonts?.serif }]}>
              {t('tours.headingRegion')}
            </Text>
            {copy.intro.map((paragraph, index) => (
              <Text key={index} style={[landingStyles.intro, { color: theme.text }]}>
                {paragraph}
              </Text>
            ))}

            {/* Links to each city's own page, not an in-page filter — see
                hub-city-chips.tsx. Cities without one are simply not listed. */}
            {listingPages.tours.length > 0 ? (
              <HubCityChips
                cityIds={listingPages.tours}
                pathname="/[lang]/[city]/tours"
                locale={locale}
              />
            ) : null}

            {loading ? (
              <Text style={[styles.status, { color: theme.mutedForeground }]}>
                {t('tours.loading')}
              </Text>
            ) : error ? (
              <Text style={[styles.status, { color: theme.destructive }]}>{error}</Text>
            ) : tours.length === 0 ? (
              <Text style={[styles.status, { color: theme.mutedForeground }]}>
                {t('tours.empty')}
              </Text>
            ) : (
              <>
                {/* Filters work after hydration only: the static HTML is always
                    the unfiltered seed (see useFilteredHub). */}
                <HubFilters
                  groups={tourFilterGroups(filters, t)}
                  onChange={setFilter}
                  onReset={resetFilters}
                />
                <HubFilterStatus
                  filtered={hub.filtered}
                  total={hub.total}
                  loading={hub.loading}
                  error={hub.error}
                  countLabel={(count) => t('hubFilters.toursCount', { count })}
                  onRetry={hub.retry}
                  onReset={resetFilters}
                />
                <View
                  aria-busy={hub.loading}
                  style={[styles.grid, hub.loading && styles.stale]}>
                  {/* The first row is above the fold, so it loads eagerly — marking
                      an LCP candidate lazy would delay it. Everything below is
                      lazy, which is the point of the raw <img> in
                      card-image.web.tsx. */}
                  {hub.rows.map((tour, index) => (
                    <TourCard key={tour.id} tour={tour} eager={index < 4} />
                  ))}
                  {more.extra.map((tour) => (
                    <TourCard key={tour.id} tour={tour} eager={false} />
                  ))}
                </View>
                {more.canLoadMore && !hub.loading ? (
                  <LoadMoreButton onPress={more.loadMore} loading={more.loading} error={more.error} />
                ) : null}
              </>
            )}

            {/* Below the cards so the LCP image stays near the top. */}
            {copy.sections.map((section) => (
              <View key={section.heading} style={landingStyles.section}>
                <SectionHeading>{section.heading}</SectionHeading>
                {section.paragraphs.map((paragraph, index) => (
                  <Text key={index} style={[landingStyles.intro, { color: theme.text }]}>
                    {paragraph}
                  </Text>
                ))}
              </View>
            ))}
            {wineTitle ? (
              <MoreLink
                label={wineTitle}
                href={{ pathname: '/[lang]/wine-tours', params: { lang: locale } }}
              />
            ) : null}

            {cityLinks.length > 0 ? (
              <View style={landingStyles.section}>
                <SectionHeading>{copy.citiesHeading}</SectionHeading>
                {cityLinks.map((link) => (
                  <MoreLink
                    key={link.id}
                    label={link.label}
                    href={{ pathname: '/[lang]/[city]/tours', params: { lang: locale, city: link.id } }}
                  />
                ))}
              </View>
            ) : null}

            <Faq heading={copy.faqHeading} entries={copy.faq} />
          </View>
        </View>
        <SiteFooter />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  centered: {
    alignItems: 'center',
    width: '100%',
  },
  page: {
    width: '100%',
    maxWidth: MaxContentWidth,
    paddingHorizontal: Spacing.three,
    paddingTop: Spacing.four,
    gap: Spacing.three,
  },
  heading: {
    fontSize: 26,
    fontWeight: '700',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.three,
    justifyContent: 'center',
  },
  status: {
    fontSize: 14,
    paddingVertical: Spacing.four,
  },
  stale: {
    opacity: 0.5,
  },
});
